import React from 'react';
import classNames from 'classnames';
import { favoritePlayerService } from 'general/js/services';
import { getRoundById } from 'general/js/helpers';
import { LazyImage } from 'project/lazy-image';
import { playerSitecoreData } from 'components/live-player';
import { CHAMPION_IMAGE_PATH } from 'general/js/constants';
import { playOffStatuses, viewTypes } from '../enums';
import LeaderboardCellPos from './leaderboard-cell-pos';
import LeaderboardCellSort from './leaderboard-cell-sort';
import LeaderboardCellPlayer from './leaderboard-cell-player';
import LeaderboardCellToPar from './leaderboard-cell-to-par';
import LeaderboardCellToday from './leaderboard-cell-today';
import LeaderboardCellTotal from './leaderboard-cell-total';
import LeaderboardCellHole from './leaderboard-cell-hole';
import LeaderboardCellScores from './leaderboard-cell-scores';
import LeaderboardCellRound from './leaderboard-cell-round';
import LeaderboardCellRoundTotal from './leaderboard-cell-round-total';

const LeaderboardRows = (
    {
        options,
        scoringCourseRound,
        scoringLeaderboard,
        currentRound,
        cutStatus,
        deviceTypeByViewport,
        players,
        playersCut,
        view,
        hasChampion,
        isPlayOffMode,
        liveScanner
    }
) => {
    const getIsPlayOff = (playerSD) => {
        if (!playerSD) return false;

        if (playerSD.playOffStatus &&
            playerSD.playOffStatus !== playOffStatuses.NOT_PLAYING
        ) {
            return true;
        }

        return !!(playerSD.suddenDeathStatus &&
            playerSD.suddenDeathStatus !== playOffStatuses.NOT_PLAYING);
    };

    const getIsLostPlayOff = (playerSD) => {
        if (!playerSD) return false;

        if (playerSD.playOffStatus &&
            playerSD.playOffStatus === playOffStatuses.LOST
        ) {
            return true;
        }

        return !!(playerSD.suddenDeathStatus &&
            playerSD.suddenDeathStatus === playOffStatuses.LOST);
    };

    const renderRows = (playersList) => {
        return playersList.map((player, index) => {
            const scoringPlayerRound = getRoundById(player.rounds, currentRound);
            const playerSD = player.sitecoreData || playerSitecoreData;
            const isPlayOff = getIsPlayOff(playerSD);
            const isLostPlayOff = getIsLostPlayOff(playerSD);
            const isFavorite = favoritePlayerService.isFavourite(player.id);

            const isLcq = options.isLastChanceQualifier;
            const isLcqChampion = (isLcq && playerSD.isLcqChampion) || false;

            const isChampion = (!isLcq && playerSD.isChampion) || false;

            const isEnhanced =
                player.isEnhancedListings || isChampion || isLcqChampion || isPlayOff || isFavorite;

            let isCut = player.status !== 1;

            if (isPlayOffMode && !hasChampion) {
                if (!isPlayOff || (isPlayOff && isLostPlayOff)) {
                    isCut = true;
                }
            }

            const rowStateCss = classNames({
                'is-cut': isCut,
                'is-leaders': view !== viewTypes.HOLE_BY_HOLE && isEnhanced,
                'is-play-off': isPlayOff,
                'is-champion': isChampion || isLcqChampion,
                'is-favorite': isFavorite,
            });
            if (playerSD.firstName) {
                return (
                    <div className={`leaderboard__row ${rowStateCss}`} key={player.id}>
                        {
                            (isChampion || isLcqChampion) &&
                            view !== viewTypes.HOLE_BY_HOLE &&
                            <LazyImage image={CHAMPION_IMAGE_PATH} />
                        }

                        <LeaderboardCellPos data={player} />
                        <LeaderboardCellSort data={player} options={options} />
                        <LeaderboardCellPlayer
                            scoringCourseRound={scoringCourseRound}
                            scoringLeaderboard={scoringLeaderboard}
                            currentRound={currentRound}
                            deviceTypeByViewport={deviceTypeByViewport}
                            player={player}
                            playerSD={playerSD}
                            view={view}
                            index={index}
                            isPlayOffMode={isPlayOffMode}
                            isEnhanced={isEnhanced}
                            isPlayOff={isPlayOff}
                            isChampion={isChampion}
                            isLcq={isLcq}
                            isLcqChampion={isLcqChampion}
                            liveScanner={liveScanner}
                        />

                        <LeaderboardCellToPar
                            player={player}
                            playerSD={playerSD}
                            view={view}
                            isPlayOff={isPlayOff}
                        />

                        {view === viewTypes.R_SCORE && <LeaderboardCellRound data={player.r1} />}
                        {view === viewTypes.R_SCORE && <LeaderboardCellRound data={player.r2} />}
                        {view === viewTypes.R_SCORE && <LeaderboardCellRound data={player.r3} />}
                        {view === viewTypes.R_SCORE && <LeaderboardCellRound data={player.r4} />}

                        {
                            view === viewTypes.HOLE_BY_HOLE &&
                            <LeaderboardCellScores
                                scoringCourseRound={scoringCourseRound}
                                scoringPlayerRound={scoringPlayerRound}
                            />
                        }

                        {view === viewTypes.SUMMARY && <LeaderboardCellHole data={player} />}

                        {view === viewTypes.SUMMARY && <LeaderboardCellToday data={player} />}
                        {view === viewTypes.R_SCORE && <LeaderboardCellTotal data={player} />}
                        {
                            view === viewTypes.HOLE_BY_HOLE &&
                            <LeaderboardCellRoundTotal
                                scoringCourseRound={scoringCourseRound}
                                player={player}
                                currentRound={currentRound} />
                        }
                    </div>
                );
            }

            return null;
        });
    };

    const renderProjectedCut = () => {
        return <div className="leaderboard__cut is-projected">PROJECTED CUT</div>;
    };

    const renderCut = () => {
        return <div className="leaderboard__cut">CUT</div>;
    };

    return (
        <React.Fragment>
            {renderRows(players)}
            {cutStatus === 'projected' && renderProjectedCut()}
            {cutStatus === 'cut' && renderCut()}
            {renderRows(playersCut)}
        </React.Fragment>
    );
};

export default LeaderboardRows;
